<template>
  <div class="ruleset-editor">
    <!-- Top-level mode: editing a full ruleset definition -->
    <template v-if="ruleset">
      <div class="ruleset-meta">
        <div class="meta-row">
          <label class="meta-label required">Name</label>
          <el-input v-model="ruleset.name" placeholder="Ruleset name" />
        </div>
        <div class="meta-row">
          <label class="meta-label required">Use Case</label>
          <el-select v-model="ruleset.useCase" placeholder="Select use case" @change="onUseCaseChange">
            <el-option v-for="option in useCaseOptions" :key="option.value" :label="option.label" :value="option.value" />
          </el-select>
        </div>
        <div class="meta-row">
          <label class="meta-label required">Infection Site</label>
          <el-select v-model="ruleset.infectionSite" placeholder="Select infection site" @change="ruleset.infectionSubtype = null">
            <el-option v-for="site in infectionSiteOptions" :key="site.value.type" :label="site.label" :value="site.value.type" />
          </el-select>
        </div>
        <div v-if="!isOutbreakDetection" class="meta-row">
          <label class="meta-label required">Infection Subtype</label>
          <el-select v-model="ruleset.infectionSubtype" placeholder="Select subtype" :disabled="!infectionSubtypeOptions.length">
            <el-option v-for="sub in infectionSubtypeOptions" :key="sub.value" :label="sub.label" :value="sub.value" />
          </el-select>
        </div>
        <div class="meta-row">
          <label class="meta-label">Description</label>
          <el-input type="textarea" v-model="ruleset.description" placeholder="Describe this ruleset" :rows="2" />
        </div>
      </div>
      <div class="ruleset-body">
        <!-- Keyed on the body's shape, not the use case: while the discard confirm is open the
             use case already reads Outbreak Detection but the body is still the tree. -->
        <div v-if="bodyIsThreshold" class="threshold-form">
          <div class="meta-row">
            <label class="meta-label required">Minimum people</label>
            <el-input-number data-testid="threshold-min-cases" v-model="ruleset.ruleset.minCases" :min="1" size="small" />
          </div>
          <div class="meta-row">
            <label class="meta-label required">Onset window (hours)</label>
            <el-input-number data-testid="threshold-window-hours" v-model="ruleset.ruleset.windowHours" :min="1" size="small" />
          </div>
          <div class="meta-row">
            <label class="meta-label required">Scope</label>
            <el-radio-group data-testid="threshold-scope" v-model="ruleset.ruleset.scope" size="small">
              <el-radio-button :label="outbreakScopes.Unit">Unit</el-radio-button>
              <el-radio-button :label="outbreakScopes.Facility">Facility</el-radio-button>
            </el-radio-group>
          </div>
          <div class="meta-row">
            <label class="meta-label required">Minimum shared symptoms</label>
            <el-input-number data-testid="threshold-min-shared-symptoms" v-model="ruleset.ruleset.minSharedSymptoms" :min="0" size="small" />
          </div>
        </div>
        <template v-else>
          <div v-if="ruleset.ruleset">
            <ruleset-editor :node="ruleset.ruleset" :infection-site="ruleset.infectionSite" @remove="ruleset.ruleset = null" />
          </div>
          <div v-else class="add-root">
            <span class="add-root-label">Add root node:</span>
            <el-button data-testid="add-root-group" size="small" @click="addRootNode(nodeTypes.CriteriaGroup)">+ Group</el-button>
            <el-button data-testid="add-root-criterion" size="small" @click="addRootNode(nodeTypes.Criterion)">+ Criterion</el-button>
          </div>
        </template>
      </div>
    </template>

    <!-- Recursive node mode -->
    <template v-if="node">
      <!-- CriteriaGroup -->
      <div v-if="node.type === nodeTypes.CriteriaGroup" class="criteria-group">
        <div class="group-header">
          <el-radio-group v-model="node.logicalOperator" size="small">
            <el-radio-button label="AND" />
            <el-radio-button label="OR" />
          </el-radio-group>
          <span class="group-caption">
            {{ node.logicalOperator === 'AND' ? 'All of the following' : 'At least one' }} must be satisfied
          </span>
          <el-button data-testid="remove-group" size="small" type="danger" plain class="remove-btn" @click="$emit('remove')">Remove
            group</el-button>
        </div>
        <ul class="children-list">
          <li v-for="(child, index) in node.children" :key="nodeKey(child)" class="children-list__item">
            <ruleset-editor :node="child" :infection-site="infectionSite" @remove="removeChild(index)" />
          </li>
        </ul>
        <div class="add-child">
          <el-button data-testid="add-child-group" size="small" @click="addChild(nodeTypes.CriteriaGroup)">+ Add Group</el-button>
          <el-button data-testid="add-child-criterion" size="small" @click="addChild(nodeTypes.Criterion)">+ Add Criterion</el-button>
        </div>
      </div>

      <!-- Criterion -->
      <div v-if="node.type === nodeTypes.Criterion" class="criterion">
        <div class="criterion-fields">
          <el-select v-model="node.criteriaType" size="small" placeholder="Criteria type" class="criteria-type-input" @change="onCriteriaTypeChange">
            <el-option :label="criteriaTypes.Symptom" :value="criteriaTypes.Symptom" />
            <el-option :label="criteriaTypes.Organism" :value="criteriaTypes.Organism" />
          </el-select>
          <el-select v-model="node.rule.operator" size="small" class="operator-select">
            <el-option v-for="op in operators" :key="op.value" :label="op.label" :value="op.value" />
          </el-select>
          <el-input-number v-model="node.rule.value" size="small" :min="0" class="value-input" />
          <el-button data-testid="remove-criterion" size="small" type="danger" plain class="remove-btn" @click="$emit('remove')">Remove</el-button>
        </div>
        <el-select v-model="node.rule.targets" multiple filterable size="small" :disabled="!node.criteriaType"
          :placeholder="isSymptomCriterion ? 'Select symptoms' : 'Select organisms'"
          class="targets-select">
          <template v-if="isSymptomCriterion">
            <el-option v-for="symptom in symptoms" :key="symptom.id" :label="symptomOptionLabel(symptom)" :value="symptom.id" />
          </template>
          <template v-else>
            <el-option v-for="organism in organisms" :key="organism.id" :label="organism.name" :value="organism.id" />
          </template>
        </el-select>
      </div>
    </template>
  </div>
</template>

<script>
import { ARCHIVED_OPTION_SUFFIX } from "./symptom.constants";
import { CriteriaType, NodeType, OUTBREAK_THRESHOLD_DEFAULTS, OutbreakScope, USE_CASE_OPTIONS, isThresholdUseCase } from "./ruleset.constants";

// Stable identity for ruleset nodes. The saved payload has no node id, and keying the
// child list by array index let Vue hand one editor instance a different node after a
// sibling was removed. That wiped the survivor's targets and left it holding the
// removed node's snapshot. A WeakMap keeps this key out of the payload entirely.
const nodeKeys = new WeakMap();
let lastNodeKey = 0;

// Confirm shown before a use-case change discards a criteria tree.
const DISCARD_TREE_TITLE = "Discard criteria?";
const DISCARD_TREE_MESSAGE = "Outbreak Detection rules use a threshold, not criteria. Switching discards this rule's criteria.";
const DISCARD_TREE_CONFIRM = "Discard";
const DISCARD_TREE_CANCEL = "Keep";

function nodeKeyFor(node) {
  if (!nodeKeys.has(node)) {
    lastNodeKey += 1;
    nodeKeys.set(node, `node-${lastNodeKey}`);
  }
  return nodeKeys.get(node);
}

export default {
  name: "RulesetEditor",
  emits: ["remove"],
  props: {
    ruleset: { type: Object, default: null },
    node: { type: Object, default: null },
    infectionSite: { type: String, default: null }
  },
  data() {
    return {
      // Symptom ids this criterion referenced when the editor opened. Only archived
      // symptoms are looked up here, so a rule that depends on one keeps showing it.
      // Reading the live `node.rule.targets` instead would drop the option the moment
      // its tag was removed, and Rules.vue has no undo.
      initialSymptomTargetIds: new Set(
        this.node?.criteriaType === CriteriaType.Symptom ? this.node.rule?.targets || [] : []
      ),
      errors: {},
      // Use case before the current change, so a declined discard can put the select back.
      lastUseCase: null,
      operators: [
        { value: "greaterThanOrEqualTo", label: "At least" },
        { value: "greaterThan", label: "More than" },
        { value: "lessThan", label: "Fewer than" },
        { value: "lessThanOrEqualTo", label: "At most" },
        { value: "equalTo", label: "Exactly" }
      ]
    };
  },
  computed: {
    criteriaTypes() {
      return CriteriaType;
    },
    nodeTypes() {
      return NodeType;
    },
    useCaseOptions() {
      return USE_CASE_OPTIONS;
    },
    outbreakScopes() {
      return OutbreakScope;
    },
    isOutbreakDetection() {
      return isThresholdUseCase(this.ruleset?.useCase);
    },
    bodyIsThreshold() {
      return this.ruleset?.ruleset?.type === NodeType.OutbreakThreshold;
    },
    isSymptomCriterion() {
      return this.node?.criteriaType === CriteriaType.Symptom;
    },
    // An archived symptom is offered only when this criterion already referenced it.
    // Every live symptom follows the ruleset's infection site as before, so changing
    // the site still drops live symptoms of the old one.
    symptoms() {
      const site = this.ruleset?.infectionSite || this.infectionSite;
      return (this.$configStore.data.symptoms || []).filter((s) => {
        if (s.archived === true) {
          return this.initialSymptomTargetIds.has(s.id);
        }
        return !site || s.classType === site;
      });
    },
    organisms() {
      return this.$configStore.data.organisms || [];
    },
    infectionSiteOptions() {
      return (this.$configStore.data.siteAndTypeOptions || [])
        .filter((e) => e.label !== "All Infection Sites")
        .filter((e) => e.value && e.value.archived !== true);
    },
    infectionSubtypeOptions() {
      if (!this.ruleset?.infectionSite) return [];
      const site = this.infectionSiteOptions.find((e) => e.value.type === this.ruleset.infectionSite);
      return (site?.children || []).filter((c) => c.label !== "All Types");
    }
  },
  watch: {
    // Rules.vue reuses this instance across dialogs, so re-read the use case whenever another
    // ruleset object is swapped in. Only the remembered value changes; the body is never touched.
    ruleset: {
      immediate: true,
      handler(ruleset) {
        this.lastUseCase = ruleset?.useCase || null;
      }
    }
  },
  methods: {
    nodeKey(node) {
      return nodeKeyFor(node);
    },
    // Targets are ids from a different table per criteria type, so a leftover organism
    // id must not be saved as a symptom target. The snapshot is kept, so a Symptom ->
    // Organism -> Symptom round trip still offers the archived symptoms this rule had.
    onCriteriaTypeChange() {
      this.node.rule.targets = [];
    },
    // Suffix goes in the label so the selected tag shows it too, not just the row.
    symptomOptionLabel(symptom) {
      return symptom.archived === true ? `${symptom.displayText}${ARCHIVED_OPTION_SUFFIX}` : symptom.displayText;
    },
    addRootNode(type) {
      this.ruleset.ruleset = this.makeNode(type);
    },
    addChild(type) {
      this.node.children.push(this.makeNode(type));
    },
    removeChild(index) {
      this.node.children.splice(index, 1);
    },
    makeNode(type) {
      if (type === NodeType.CriteriaGroup) {
        return { type: NodeType.CriteriaGroup, logicalOperator: "AND", children: [] };
      }
      return {
        type: NodeType.Criterion,
        criteriaType: "",
        rule: { operator: "greaterThanOrEqualTo", value: 1, targets: [] }
      };
    },
    makeThresholdNode() {
      return { type: NodeType.OutbreakThreshold, ...OUTBREAK_THRESHOLD_DEFAULTS };
    },
    // Tree and threshold bodies are not convertible, so a use-case change that crosses that
    // boundary drops the body; switching between the two tree use cases keeps it. Keyed on the
    // body's current shape (not on the previous use case, and not via a watcher) so that opening
    // an existing ruleset for editing never touches its body. Dropping a criteria tree is
    // confirmed first: it cannot be rebuilt from a threshold and Rules.vue has no undo. A
    // threshold is four fields, so dropping one is not.
    async onUseCaseChange(useCase) {
      const body = this.ruleset.ruleset;
      const bodyIsThreshold = body?.type === NodeType.OutbreakThreshold;
      const discardsTree = isThresholdUseCase(useCase) && !!body && !bodyIsThreshold;
      if (discardsTree && !(await this.confirmDiscardTree())) {
        this.ruleset.useCase = this.lastUseCase;
        return;
      }
      this.lastUseCase = useCase;
      if (isThresholdUseCase(useCase)) {
        // The API rejects any subtype (including "") for outbreak detection.
        this.ruleset.infectionSubtype = null;
        if (!bodyIsThreshold) {
          this.ruleset.ruleset = this.makeThresholdNode();
        }
      } else if (bodyIsThreshold) {
        this.ruleset.ruleset = null;
      }
    },
    confirmDiscardTree() {
      return this.$confirm(DISCARD_TREE_MESSAGE, DISCARD_TREE_TITLE, {
        confirmButtonText: DISCARD_TREE_CONFIRM,
        cancelButtonText: DISCARD_TREE_CANCEL,
        type: "warning"
      }).then(
        () => true,
        () => false
      );
    }
  }
};
</script>

<style scoped>
.required::after {
  content: "*";
  color: #c00;
}

.ruleset-editor {
  font-size: 0.9rem;
  color: #333;
}

/* Top-level meta fields */
.ruleset-meta {
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  padding: 0.75rem;
  background: #f5f5f5;
  margin-bottom: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.meta-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.meta-label {
  font-size: 0.8rem;
  font-weight: bold;
  color: #555;
  min-width: 120px;
  padding-top: 0.4rem;
}

.ruleset-body {
  padding: 0.75rem;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  background: #fff;
}

/* Add root placeholder */
.add-root {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border: 1px dashed #ccc;
  border-radius: 4px;
  color: #888;
}

.add-root-label {
  font-size: 0.8rem;
}

/* Outbreak threshold form */
.threshold-form {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.threshold-form .meta-row {
  align-items: center;
}

.threshold-form .meta-label {
  min-width: 190px;
  padding-top: 0;
}

/* As a flex item the group is blockified and shrink-wrapped a hair too narrow, wrapping the
   second button onto its own line; laying the buttons out as flex items keeps them side by side. */
.threshold-form .el-radio-group {
  display: flex;
}

/* CriteriaGroup */
.criteria-group {
  border-left: 3px solid #7aabdc;
  padding: 0.5rem 0 0.5rem 0.75rem;
  margin-bottom: 0.4rem;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.group-caption {
  font-size: 0.78rem;
  color: #555;
  flex: 1;
}

/* Children list */
.children-list {
  list-style: none;
  margin: 0 0 0.5rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.children-list__item {
  margin: 0;
  padding: 0;
}

/* Add child */
.add-child {
  display: flex;
  gap: 0.4rem;
}

/* Criterion */
.criterion {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 0.5rem 0.6rem;
  background: #fafafa;
}

.criterion-fields {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.4rem;
  flex-wrap: wrap;
}

.criteria-type-input {
  width: 160px;
}

.operator-select {
  width: 160px;
}

.value-input {
  width: 110px;
}

.remove-btn {
  margin-left: auto;
}

.targets-select {
  width: 100%;
}
</style>
